import { useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import {
  useIntegration,
  useUpdateIntegration,
  useDeleteIntegration,
  useProviders,
  useIntegrationEvents,
  useIntegrationEventDetail,
  useIntegrationRedeliver,
} from '../../hooks/useIntegrations';
import {
  Badge,
  Button,
  Card,
  CardHeader,
  ConfirmModal,
  CopyButton,
  Input,
  Modal,
  ProviderLogo,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  getStatusVariant,
} from '@/components/ui';
import { useToast } from '../../components/ui/Toast';
import { useSpaceContext } from '../../contexts/SpaceContext';
import { useAuth } from '../../store/auth-context';
import { meetsRole } from '../../lib/space-roles';
import { routes } from '../../lib/routes';
import { getProviderFallback } from '../../lib/provider-instructions';
import { isSlackIntegration } from '../../lib/slack-integration';
import SlackBotCredentialsPanel from './SlackBotCredentialsPanel';
import { AlertTriangle, BookOpen, ChevronDown, ChevronRight, KeyRound, Pause, Play, RotateCcw } from 'lucide-react';
import IntegrationDeliveriesCard from './IntegrationDeliveriesCard';
import SetupGuidePanel from './SetupGuidePanel';
import QuickSetupPanel from './QuickSetupPanel';
import { formatRelativeTime } from '../activity/utils';
import HintBanner from './HintBanner';
import { getHintEntries } from '../../lib/integration-hints';
import BackLink from '../../components/navigation/BackLink';

export default function IntegrationDetail() {
  const { integrationId } = useParams<{ integrationId: string }>();
  const navigate = useNavigate();
  const { spaceId, space, role } = useSpaceContext();
  const { user } = useAuth();
  const { success, error: showError } = useToast();

  const { data: integration, isLoading, error } = useIntegration(integrationId);
  const { data: providers } = useProviders();
  const updateIntegration = useUpdateIntegration();
  const deleteIntegration = useDeleteIntegration();

  const [deleteModal, setDeleteModal] = useState(false);
  const [rotatedSecret, setRotatedSecret] = useState<string | null>(null);
  const [updateSecretModal, setUpdateSecretModal] = useState(false);
  const [newSecretInput, setNewSecretInput] = useState('');
  const [showSetupGuide, setShowSetupGuide] = useState(false);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (error || !integration) {
    return (
      <div className="text-center py-12">
        <p className="text-red-600">Failed to load integration</p>
        <Link to={routes.console.spaceIntegrations(spaceId)} className="text-primary-600 hover:text-primary-500 mt-2 inline-block">
          Back to integrations
        </Link>
      </div>
    );
  }

  const providerInfo = providers?.find((p) => p.name === integration.provider);
  const providerDisplayName =
    providerInfo?.displayName ?? getProviderFallback(integration.provider).displayName;
  const requiresUserSecret = providerInfo?.secretMode === 'provider-issued';
  const secretLabel = providerInfo?.secretField?.label ?? 'Webhook secret';
  const secretHelp =
    providerInfo?.secretField?.helpText ??
    'Paste the new secret issued by the provider.';

  const handleDelete = async () => {
    try {
      await deleteIntegration.mutateAsync(integration.integrationId);
      success('Integration deleted successfully');
      navigate(routes.console.spaceIntegrations(spaceId));
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to delete integration');
    }
  };

  const handleToggleStatus = async () => {
    const newStatus = integration.status === 'active' ? 'paused' : 'active';
    try {
      await updateIntegration.mutateAsync({
        integrationId: integration.integrationId,
        input: { status: newStatus },
      });
      success(`Integration ${newStatus === 'active' ? 'resumed' : 'paused'} successfully`);
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to update integration');
    }
  };

  const handleRotateSecret = async () => {
    try {
      const updated = await updateIntegration.mutateAsync({
        integrationId: integration.integrationId,
        input: { rotateSecret: true },
      });
      setRotatedSecret(updated.webhookSecret ?? null);
      success('Webhook secret rotated successfully');
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to rotate secret');
    }
  };

  const handleUpdateSecret = async () => {
    try {
      await updateIntegration.mutateAsync({
        integrationId: integration.integrationId,
        input: { webhookSecret: newSecretInput.trim() },
      });
      success('Signing secret updated');
      setUpdateSecretModal(false);
      setNewSecretInput('');
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to update secret');
    }
  };

  return (
    <div className="space-y-6">
      <BackLink to={routes.console.spaceIntegrations(spaceId)}>Integrations</BackLink>

      {/* Header */}
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-4">
          <ProviderLogo
            slug={integration.provider}
            displayName={providerDisplayName}
            className="h-12 w-12"
            fallbackClassName="text-sm"
          />
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold text-foreground">{integration.displayName}</h1>
              <Badge variant={getStatusVariant(integration.status)}>{integration.status}</Badge>
            </div>
            <p className="mt-1 text-sm text-muted-foreground">
              {providerDisplayName} integration
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button
            variant="secondary"
            onClick={handleToggleStatus}
            isLoading={updateIntegration.isPending}
          >
            {integration.status === 'active' ? (
              <><Pause className="h-4 w-4 mr-2" /> Pause</>
            ) : (
              <><Play className="h-4 w-4 mr-2" /> Resume</>
            )}
          </Button>
        </div>
      </div>

      {/* Hints */}
      {getHintEntries(integration).length > 0 && (
        <div className="space-y-3">
          {getHintEntries(integration).map(([code, hint]) => (
            <HintBanner key={code} code={code} hint={hint} />
          ))}
        </div>
      )}

      {/* Rotated secret alert — provider-issued secrets never surface a new
          value back (the user owns it), so this only ever fires for
          Optr-generated rotations. */}
      {rotatedSecret && !requiresUserSecret && (
        <div className="rounded-lg border border-yellow-300 bg-yellow-50 dark:border-yellow-700 dark:bg-yellow-900/20 p-4 flex gap-3">
          <AlertTriangle className="h-5 w-5 text-yellow-600 dark:text-yellow-400 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-sm font-medium text-yellow-800 dark:text-yellow-200">
              New webhook secret — save it now
            </p>
            <div className="flex items-center gap-3 mt-2">
              <code className="text-sm bg-white dark:bg-black/20 px-3 py-1.5 rounded-md font-mono break-all flex-1">
                {rotatedSecret}
              </code>
              <CopyButton value={rotatedSecret} size="md" />
            </div>
            <p className="text-xs text-yellow-700 dark:text-yellow-300 mt-2">
              Update this secret in your provider's webhook settings. The old secret will no longer validate.
            </p>
            <button
              onClick={() => setRotatedSecret(null)}
              className="text-xs text-yellow-700 dark:text-yellow-300 underline mt-1"
            >
              Dismiss
            </button>
          </div>
        </div>
      )}

      {/* Configuration card */}
      <Card>
        <CardHeader title="Configuration" />
        <dl className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <dt className="text-sm font-medium text-muted-foreground">Provider</dt>
            <dd className="mt-1 text-sm text-foreground">{providerDisplayName}</dd>
          </div>
          <div>
            <dt className="text-sm font-medium text-muted-foreground">Ingestion Type</dt>
            <dd className="mt-1 text-sm text-foreground">{integration.ingestionType}</dd>
          </div>
          <div className="md:col-span-2">
            <dt className="text-sm font-medium text-muted-foreground">Webhook URL</dt>
            <dd className="mt-1 flex items-center gap-3">
              <code className="text-sm bg-muted px-3 py-2 rounded-md flex-1 font-mono break-all">
                {integration.webhookUrl}
              </code>
              <CopyButton value={integration.webhookUrl} size="md" />
            </dd>
          </div>
          {integration.eventFilters && integration.eventFilters.length > 0 && (
            <div className="md:col-span-2">
              <dt className="text-sm font-medium text-muted-foreground">Event Filters</dt>
              <dd className="mt-1 flex flex-wrap gap-2">
                {integration.eventFilters.map((filter) => (
                  <Badge key={filter} variant="gray">{filter}</Badge>
                ))}
              </dd>
            </div>
          )}
        </dl>
      </Card>

      {/* Statistics */}
      <Card>
        <CardHeader title="Statistics" />
        <dl className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div>
            <dt className="text-sm font-medium text-muted-foreground">Total Events</dt>
            <dd className="mt-1 text-2xl font-semibold text-foreground">{integration.eventCount}</dd>
          </div>
          <div>
            <dt className="text-sm font-medium text-muted-foreground">Errors</dt>
            <dd className={`mt-1 text-2xl font-semibold ${integration.errorCount > 0 ? 'text-red-600 dark:text-red-400' : 'text-foreground'}`}>
              {integration.errorCount}
            </dd>
            {integration.errorCount > 0 && (
              integration.lastErrorAt ? (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <p className="mt-1 text-xs text-muted-foreground cursor-default w-fit">
                      Last error: {formatRelativeTime(integration.lastErrorAt)}
                    </p>
                  </TooltipTrigger>
                  <TooltipContent className="text-xs">
                    {new Date(integration.lastErrorAt).toLocaleString()}
                  </TooltipContent>
                </Tooltip>
              ) : (
                <p className="mt-1 text-xs text-muted-foreground">Last error: unknown</p>
              )
            )}
          </div>
          <div>
            <dt className="text-sm font-medium text-muted-foreground">Last Event</dt>
            <dd className="mt-1 text-sm text-foreground">
              {integration.lastEventAt ? new Date(integration.lastEventAt).toLocaleString() : 'Never'}
            </dd>
          </div>
          <div>
            <dt className="text-sm font-medium text-muted-foreground">Created</dt>
            <dd className="mt-1 text-sm text-foreground">
              {new Date(integration.createdAt).toLocaleString()}
            </dd>
          </div>
        </dl>
      </Card>

      <QuickSetupPanel
        provider={integration.provider}
        providerDisplayName={providerDisplayName}
        documentationUrl={providerInfo?.documentationUrl}
        integration={integration}
      />

      {/* Slack bot tokens — one credential per workspace at this space's scope */}
      {isSlackIntegration(integration) && (
        <SlackBotCredentialsPanel
          scope={{ kind: 'space', spaceId, spaceName: space?.name ?? spaceId }}
          canManage={user?.role === 'admin' || meetsRole(role, 'admin')}
        />
      )}

      {/* Actions */}
      <Card>
        <CardHeader
          title="Actions"
          description="Manage webhook secret and integration state"
        />
        <div className="flex gap-3">
          {requiresUserSecret ? (
            <Button
              variant="secondary"
              onClick={() => setUpdateSecretModal(true)}
            >
              <KeyRound className="h-4 w-4 mr-2" />
              Update Signing Secret
            </Button>
          ) : (
            <Button
              variant="secondary"
              onClick={handleRotateSecret}
              isLoading={updateIntegration.isPending}
            >
              <RotateCcw className="h-4 w-4 mr-2" />
              Rotate Secret
            </Button>
          )}
        </div>
      </Card>

      {/* Setup guide (collapsible) */}
      {!showSetupGuide ? (
        <Card>
          <button
            type="button"
            onClick={() => setShowSetupGuide(true)}
            className="w-full flex items-center justify-between text-left"
          >
            <div className="flex items-center gap-2">
              <BookOpen className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm font-semibold text-foreground">Setup Guide</span>
              <span className="text-xs text-muted-foreground">
                Webhook configuration instructions
              </span>
            </div>
            <ChevronRight className="h-4 w-4 text-muted-foreground" />
          </button>
        </Card>
      ) : (
        <div className="space-y-2">
          <button
            type="button"
            onClick={() => setShowSetupGuide(false)}
            className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
          >
            <ChevronDown className="h-4 w-4" />
            Hide Setup Guide
          </button>
          <SetupGuidePanel
            provider={integration.provider}
            providerDisplayName={providerDisplayName}
            documentationUrl={providerInfo?.documentationUrl}
            integration={integration}
          />
        </div>
      )}

      {/* Events log */}
      <IntegrationDeliveriesCard
        integrationId={integration.integrationId}
        useEvents={useIntegrationEvents}
        useEventDetail={useIntegrationEventDetail}
        useRedeliver={useIntegrationRedeliver}
        queryKeyPrefix={['space', spaceId, 'integrations', integration.integrationId]}
      />

      {/* Danger zone */}
      <Card className="border-red-200 dark:border-red-800">
        <CardHeader
          title="Danger Zone"
          description="Irreversible and destructive actions"
        />
        <Button variant="danger" onClick={() => setDeleteModal(true)}>
          Delete Integration
        </Button>
      </Card>

      {/* Delete confirmation modal */}
      <ConfirmModal
        isOpen={deleteModal}
        onClose={() => setDeleteModal(false)}
        onConfirm={handleDelete}
        title="Delete Integration"
        message={`Are you sure you want to delete "${integration.displayName}"? Incoming webhooks will stop being processed. This action cannot be undone.`}
        confirmText="Delete"
        isLoading={deleteIntegration.isPending}
      />

      {/* Update signing secret modal (provider-issued only) */}
      <Modal
        isOpen={updateSecretModal}
        onClose={() => {
          setUpdateSecretModal(false);
          setNewSecretInput('');
        }}
        title="Update Signing Secret"
        size="md"
        footer={
          <>
            <Button
              variant="outline"
              onClick={() => {
                setUpdateSecretModal(false);
                setNewSecretInput('');
              }}
              disabled={updateIntegration.isPending}
            >
              Cancel
            </Button>
            <Button
              onClick={handleUpdateSecret}
              isLoading={updateIntegration.isPending}
              disabled={!newSecretInput.trim()}
            >
              Update
            </Button>
          </>
        }
      >
        <Input
          label={secretLabel}
          type="password"
          value={newSecretInput}
          onChange={(e: { target: { value: string } }) =>
            setNewSecretInput(e.target.value)
          }
          placeholder="Paste new signing secret"
          hint={secretHelp}
        />
      </Modal>
    </div>
  );
}
